import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '../config';
import {
  IAddAddressPostRequestPayload,
  IAddAddressResponse,
  IAddressesResponse,
  AddressType,
  IEditAddressPutRequestPayload,
  IEditAddressPutResponse,
} from '../../types/innerLayout/addresses';
import { toast } from 'react-toastify';

interface IAddressesService {
  listAddresses: (address_type: AddressType) => Promise<IAddressesResponse>;
  addAddress: (
    payload: IAddAddressPostRequestPayload
  ) => Promise<IAddAddressResponse>;
  editAddress: (
    payload: IEditAddressPutRequestPayload
  ) => Promise<IEditAddressPutResponse>;
  deleteAddress: (id: number, addressType: AddressType) => Promise<void>;
}

export const addresses: IAddressesService = {
  listAddresses: (type) =>
    http.get(`/api/auth/customer_address/get_address?type=${type}`),
  addAddress: (payload) =>
    http.post(
      `/api/auth/customer_address/billing_address?type=${payload.address_type}`,
      payload.body
    ),
  editAddress: (payload) =>
    http.put(
      `/api/auth/customer_address/update/${payload.id}?type=${payload.address_type}`,
      payload.body
    ),
  deleteAddress: (id, addressType) =>
    http.delete(`/api/auth/customer_address/destroy/${id}?type=${addressType}`),
};

export const useAddresses = (address_type: AddressType, enabled:boolean = true) => {
  return useQuery({
    queryKey: ['list-addresses', address_type],
    queryFn: () => addresses.listAddresses(address_type),
    enabled,
  });
};

export const useAddAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['add-address'],
    mutationFn: addresses.addAddress,
    onSuccess: (res) => {
      toast.success('Address Added successfully'),
        queryClient.invalidateQueries({
          queryKey: res.address_type ? ['list-addresses', res.address_type]: ['list-addresses'],
        });
    },
    onError: () => toast.error('Add failed'),
  });
};

export const useEditAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['edit-address'],
    mutationFn: (payload: IEditAddressPutRequestPayload) =>
      addresses.editAddress(payload),
    onSuccess: (res) => {
      toast.success('Address updated successfully'),
        queryClient.invalidateQueries({
          queryKey: ['list-addresses', res.address_type],
        });
    },
    onError: () => toast.error('Update failed'),
  });
};

export const useDeleteAddress = (id: number, addressType: AddressType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['delete-address'],
    mutationFn: () => addresses.deleteAddress(id, addressType),
    onSuccess: () => {
      toast.success('Address deleted successfully'),
        queryClient.invalidateQueries({
          queryKey: ['list-addresses', addressType],
        });
    },
    onError: () => toast.error('Delete failed'),
  });
};
